import { noop } from "lodash";
import { createContext, FC, useEffect, useState } from "react";
import { Option } from "space-monad";
import { VerificationDTO } from "../../../../../store/verification/models/dto/due-diligence";
import { DueDiligenceFormStatus } from "../../../../../store/verification/models/internal";
import { VerificationService } from "../../../../../store/verification/service";

export type ConfigContext = {
  item: VerificationDTO.Responses.DueDiligence.Config.Get.T;
  state: {
    kycToFillIn: number;
    setKycToFillIn: (x: number) => void;
    dueDiligenceId?: string;
    setDueDiligenceId: (x?: string) => void;
    contactEmail?: string;
    setContactEmail: (x?: string) => void;
    code?: string;
    status?: typeof DueDiligenceFormStatus.T;
  };
};

export const ConfigContext = createContext<ConfigContext>(
  (undefined as any) as ConfigContext
);

export const ConfigContextProvider: FC<{
  item: VerificationDTO.Responses.DueDiligence.Config.Get.T;
  code?: string;
}> = ({ children, item, code }) => {
  const [kycToFillIn, setKycToFillIn] = useState<number>(0);
  const [dueDiligenceId, setDueDiligenceId] = useState<string | undefined>(
    item.dueDiligenceId
  );
  const [dueDiligenceStatus, setDueDiligenceStatus] = useState<
    typeof DueDiligenceFormStatus.T | undefined
  >();

  const initialisedKyc = useInitialisedKycDetails(item.dueDiligenceId);
  const [contactEmail, setContactEmail] = useState<string | undefined>();

  useEffect(() => {
    setContactEmail(initialisedKyc?.contactDetails.emailAddress);
  }, [initialisedKyc?.contactDetails.emailAddress]);

  useEffect(() => {
    setDueDiligenceStatus(initialisedKyc?.status);
  }, [initialisedKyc?.status]);

  useEffect(() => {
    if (initialisedKyc?.totalKycToBeProcessed)
      setKycToFillIn(
        initialisedKyc.totalKycToBeProcessed -
          Option(initialisedKyc.participants)
            .map((x) => x.length)
            .getOrElse(0)
      );
  }, [initialisedKyc?.totalKycToBeProcessed]);

  return (
    <ConfigContext.Provider
      value={{
        item,
        state: {
          kycToFillIn,
          setKycToFillIn,
          dueDiligenceId,
          setDueDiligenceId,
          contactEmail,
          setContactEmail,
          status: dueDiligenceStatus,
          code,
        },
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

const useInitialisedKycDetails = (dueDiligenceId?: string) => {
  const [
    data,
    setData,
  ] = useState<VerificationDTO.Responses.DueDiligence.Form.GetPublic.T>();

  useEffect(() => {
    if (dueDiligenceId) {
      VerificationService.getPublicSingleDueDiligenceForm(dueDiligenceId)
        .then((data) => {
          setData(data);
        })
        .catch(noop);
    }
  }, [dueDiligenceId]);

  return data;
};
